import React from "react";
import { Link } from "react-router-dom";
import { m, useReducedMotion } from "framer-motion";
import {
  ArrowRight,
  Bot,
  BusFront,
  CalendarCheck,
  ClipboardCheck,
  FileCheck2,
  Megaphone,
  ReceiptIndianRupee,
  ShieldCheck,
  Smartphone,
} from "lucide-react";
import { BrandCopy } from "../../components/BrandWordmarkText";
import { MarketingAssetSlot } from "../../components/marketing/MarketingAssetSlot";
import { useMarketingMotion } from "../../components/marketing/MarketingMotion";

const EASE = [0.16, 1, 0.3, 1];

const FALLBACK_CONTENT = Object.freeze({
  badge: "School Mobile App",
  heading: "Keep every family connected.",
  subhead: "Parents check attendance, fees and results; teachers mark attendance and homework — in the early-access apps for families and school teams.",
  bullets: Object.freeze([
    "Parents see their child's attendance and the alerts the school has switched on",
    "Invoice-led fee payment, history and authenticated receipts",
    "Read-only family views for homework, timetable and published results",
    "Pinned notices, events and typed inbox updates",
    "Teacher attendance, homework and marks workflows",
    "School-leader KPIs and governed approval queues",
  ]),
});

export const LANDING_MOBILE_SCREENS = Object.freeze([
  Object.freeze({
    key: "tasks",
    assetKey: "screenTaskDetail",
    label: "Operations task",
    alt: "Vidyapeeth360 operations task screen showing a synthetic lab-safety checklist and proof controls.",
  }),
  Object.freeze({
    key: "aira",
    assetKey: "screenAira",
    label: "Aira assistant",
    alt: "Vidyapeeth360 Aira screen showing sample prompts for fees, attendance, marks and transport.",
  }),
  Object.freeze({
    key: "security",
    assetKey: "screenSecurityOverview",
    label: "Account security",
    alt: "Vidyapeeth360 account security screen showing password, authenticator and notification-device controls.",
  }),
]);

export const LANDING_MOBILE_ROLES = Object.freeze([
  Object.freeze({ key: "student", title: "Student", icon: FileCheck2 }),
  Object.freeze({ key: "guardian", title: "Guardian", icon: ReceiptIndianRupee }),
  Object.freeze({ key: "teacher", title: "Teacher", icon: CalendarCheck }),
  Object.freeze({ key: "admin", title: "School leader", icon: ShieldCheck }),
]);

const WORKFLOW_NODES = Object.freeze([
  Object.freeze({ key: "fees", label: "Fees", icon: ReceiptIndianRupee, position: "left-[3%] top-[12%] sm:left-[5%]" }),
  Object.freeze({ key: "attendance", label: "Attendance", icon: CalendarCheck, position: "right-[2%] top-[16%] sm:right-[5%]" }),
  Object.freeze({ key: "results", label: "Results", icon: FileCheck2, position: "left-[1%] top-[43%] sm:left-[3%]" }),
  Object.freeze({ key: "transport", label: "Transport", icon: BusFront, position: "right-[1%] top-[45%] sm:right-[3%]" }),
  Object.freeze({ key: "tasks", label: "Tasks", icon: ClipboardCheck, position: "left-[4%] bottom-[13%] sm:left-[8%]" }),
  Object.freeze({ key: "notices", label: "Notices", icon: Megaphone, position: "right-[4%] bottom-[12%] sm:right-[8%]" }),
]);

function SafeReveal({ children, className = "", delay = 0 }) {
  const reducedMotion = useReducedMotion();
  const { paused } = useMarketingMotion();
  const motionOff = Boolean(reducedMotion || paused);

  return (
    <m.div
      className={className}
      initial={motionOff ? false : { opacity: 0, transform: "translate3d(0, 16px, 0)" }}
      whileInView={motionOff ? undefined : { opacity: 1, transform: "translate3d(0, 0, 0)" }}
      viewport={motionOff ? undefined : { once: true, amount: 0.18 }}
      transition={{ duration: motionOff ? 0 : 0.26, delay: motionOff ? 0 : delay, ease: EASE }}
    >
      {children}
    </m.div>
  );
}

function ProductPhone({ screen, className = "", priority = false, testId }) {
  return (
    <figure className={className} data-testid={testId}>
      <div className="relative rounded-[2.1rem] border border-line-strong bg-[var(--vp-hero-navy)] p-2 shadow-[0_28px_70px_-25px_rgba(10,42,91,0.55)]">
        <span className="pointer-events-none absolute left-1/2 top-3 z-20 h-1.5 w-12 -translate-x-1/2 rounded-full bg-[var(--vp-hero-navy)]" aria-hidden="true" />
        <MarketingAssetSlot
          assetKey={screen.assetKey}
          alt={screen.alt}
          priority={priority}
          fit="contain"
          sizes={priority ? "(min-width: 1024px) 270px, 58vw" : "(min-width: 1024px) 190px, 42vw"}
          className="!rounded-[1.58rem] !border-0 bg-white"
        />
      </div>
    </figure>
  );
}

function MobileProductComposition() {
  const reducedMotion = useReducedMotion();
  const { paused } = useMarketingMotion();
  const motionOff = Boolean(reducedMotion || paused);

  return (
    <div
      className="relative mx-auto h-[39rem] w-full max-w-[37rem] sm:h-[41rem] lg:h-[43rem]"
      role="group"
      aria-label="Vidyapeeth360 mobile app screens, shown with the school workflows they connect"
      data-testid="landing-mobile-product-composition"
      data-motion-state={motionOff ? "off" : "running"}
    >
      <div className="pointer-events-none absolute left-1/2 top-[48%] h-[24rem] w-[24rem] -translate-x-1/2 -translate-y-1/2 rounded-full border border-primary/15 bg-primary/10 sm:h-[31rem] sm:w-[31rem]" aria-hidden="true" />
      <div className="pointer-events-none absolute left-1/2 top-[48%] h-[17rem] w-[17rem] -translate-x-1/2 -translate-y-1/2 rounded-full border border-primary/20 sm:h-[23rem] sm:w-[23rem]" aria-hidden="true" />

      {WORKFLOW_NODES.map((node, index) => {
        const Icon = node.icon;
        return (
          <div key={node.key} className={`absolute z-30 ${node.position}`} aria-hidden="true">
            <div
              className="mobile-workflow-node flex min-h-11 items-center gap-2 rounded-2xl border border-line bg-surface px-3 py-2 text-[11px] font-bold text-ink-2 shadow-card sm:px-3.5 sm:text-xs"
              style={{ animationDelay: `${-0.65 * index}s` }}
            >
              <span className="grid h-7 w-7 place-items-center rounded-lg border border-primary/20 bg-primary-50 text-primary">
                <Icon className="h-3.5 w-3.5" strokeWidth={1.9} aria-hidden="true" />
              </span>
              {node.label}
            </div>
          </div>
        );
      })}

      <div className="absolute left-[10%] top-[24%] z-10 w-[8rem] -rotate-[6deg] sm:left-[13%] sm:w-[10.25rem]">
        <SafeReveal delay={0.05}>
          <ProductPhone screen={LANDING_MOBILE_SCREENS[0]} testId="landing-mobile-phone-tasks" />
        </SafeReveal>
      </div>
      <div className="absolute right-[10%] top-[24%] z-10 w-[8rem] rotate-[6deg] sm:right-[13%] sm:w-[10.25rem]">
        <SafeReveal delay={0.1}>
          <ProductPhone screen={LANDING_MOBILE_SCREENS[2]} testId="landing-mobile-phone-security" />
        </SafeReveal>
      </div>
      <div className="absolute left-1/2 top-[7%] z-20 w-[12.25rem] -translate-x-1/2 sm:w-[12.5625rem]">
        <SafeReveal>
          <ProductPhone screen={LANDING_MOBILE_SCREENS[1]} priority testId="landing-mobile-phone-aira" />
        </SafeReveal>
      </div>

      <div className="absolute inset-x-4 bottom-2 z-30 flex flex-col items-center gap-1.5 text-center">
        <span className="inline-flex min-h-8 items-center gap-2 rounded-full border border-positive/25 bg-surface px-3 text-[10px] font-bold uppercase tracking-[0.13em] text-positive shadow-card">
          <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
          Real product UI
        </span>
        <p className="text-[11px] font-semibold text-ink-subtle">First-party mobile captures <span aria-hidden="true">·</span> synthetic sample data</p>
      </div>
    </div>
  );
}

export default function LandingMobileAppSection({ content }) {
  const reducedMotion = useReducedMotion();
  const { paused } = useMarketingMotion();
  const motionOff = Boolean(reducedMotion || paused);
  const c = content || {};
  const bullets = Array.isArray(c.bullets) && c.bullets.length ? c.bullets : FALLBACK_CONTENT.bullets;

  return (
    <section
      id="mobile-app"
      className="relative scroll-mt-24 overflow-hidden border-b border-line bg-muted"
      aria-labelledby="landing-mobile-heading"
      data-testid="landing-mobile-app"
      data-motion-state={motionOff ? "off" : "ready"}
    >
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-primary/40" aria-hidden="true" />
      <div className="mx-auto max-w-7xl px-5 py-16 lg:py-24">
        <SafeReveal className="grid gap-6 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-7">
            <p className="inline-flex min-h-8 items-center gap-2 rounded-full border border-orange-border bg-orange-soft px-3 text-xs font-bold uppercase tracking-[0.14em] text-orange-text">
              <Smartphone className="h-3.5 w-3.5" aria-hidden="true" />
              <BrandCopy>{c.badge || FALLBACK_CONTENT.badge}</BrandCopy>
            </p>
            <h2 id="landing-mobile-heading" className="mt-5 max-w-[17ch] text-balance font-heading text-3xl font-extrabold leading-[1.06] tracking-[-0.03em] text-ink sm:text-4xl lg:text-5xl">
              <BrandCopy>{c.heading || FALLBACK_CONTENT.heading}</BrandCopy>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pb-1">
            <p className="max-w-[58ch] text-pretty text-base leading-relaxed text-ink-2 sm:text-lg">
              <BrandCopy>{c.subhead || FALLBACK_CONTENT.subhead}</BrandCopy>
            </p>
            <div className="mt-5 flex flex-wrap gap-2 text-xs font-semibold">
              <span className="inline-flex min-h-8 items-center gap-1.5 rounded-full border border-positive/25 bg-positive-soft px-3 text-positive">
                <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" /> Responsive portals available
              </span>
              <span className="inline-flex min-h-8 items-center gap-1.5 rounded-full border border-primary/25 bg-primary-50 px-3 text-primary">
                Native app in controlled early access
              </span>
            </div>
          </div>
        </SafeReveal>

        <div className="mt-12 overflow-hidden rounded-[2rem] border border-line bg-surface shadow-elevated lg:grid lg:grid-cols-[minmax(0,1.08fr)_minmax(20rem,0.92fr)] lg:items-stretch">
          <SafeReveal className="relative min-w-0 overflow-hidden bg-primary-50 px-2 py-6 sm:px-7 lg:min-h-[49rem] lg:px-4" delay={0.03}>
            <MobileProductComposition />
          </SafeReveal>

          <SafeReveal className="min-w-0 p-6 sm:p-9 lg:flex lg:flex-col lg:justify-center lg:p-11" delay={0.07}>
            <p className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-primary">
              <Bot className="h-4 w-4" aria-hidden="true" /> One connected mobile layer
            </p>
            <h3 className="mt-4 max-w-[15ch] font-heading text-3xl font-bold leading-tight tracking-tight text-ink sm:text-4xl">
              Ask, act and approve without losing context.
            </h3>
            <p className="mt-5 max-w-[52ch] text-base leading-relaxed text-ink-2">
              Aira answers a family&rsquo;s routine questions in one place. Support staff record on-site work with photo proof against the task it belongs to, and every person can see and manage their own account security from the app.
            </p>

            <ul className="mt-8 grid gap-x-5 gap-y-3.5 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2" aria-label="Mobile workflow scope">
              {bullets.slice(0, 6).map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-[13px] leading-relaxed text-ink-2">
                  <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-primary/10 text-primary">
                    <ShieldCheck className="h-3 w-3" strokeWidth={2.2} aria-hidden="true" />
                  </span>
                  <BrandCopy>{item}</BrandCopy>
                </li>
              ))}
            </ul>

            <div className="mt-9 rounded-2xl border border-line bg-muted p-4">
              <p className="text-xs font-bold uppercase tracking-[0.13em] text-ink-subtle">Availability</p>
              <p className="mt-2 text-sm leading-relaxed text-ink-2">
                Native apps are in controlled early access. Public store availability follows school review and staged rollout.
              </p>
            </div>

            <Link
              to="/mobile-app"
              className="group mt-8 inline-flex min-h-12 w-fit items-center gap-2 rounded-full bg-primary px-6 text-sm font-semibold text-primary-fg transition-[background-color,transform,box-shadow] duration-200 hover:bg-primary-700 hover:shadow-elevated active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focusring/60"
            >
              Explore mobile workflows
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" aria-hidden="true" />
            </Link>
          </SafeReveal>
        </div>
      </div>
    </section>
  );
}
